// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Selector.tsx
 * @input Uses React, StyleX, usePopover, useTooltip, Icon, InputGroupContext,
 *   and Selector positioning hooks
 * @output Exports Selector component
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Selector/Selector.doc.mjs
 * - /packages/core/src/Selector/Selector.test.tsx
 * - /packages/core/src/Selector/index.ts
 * - /apps/storybook/stories/InputGroup.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/Selector/ (showcase blocks)
 */
import React, { useCallback, useEffect, useId, useMemo, useOptimistic, useRef, useState, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import { usePopover } from "../Popover/usePopover.js";
import { useTooltip } from "../Tooltip/index.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { Field, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputWrapperStyles } from "../Field/index.js";
import { Divider } from "../Divider/index.js";
import { layerAnimations } from "../Layer/layerAnimations.stylex.js";
import { Spinner } from "../Spinner/index.js";
import { TextInput } from "../TextInput/index.js";
import { useAnnounce } from "../hooks/useAnnounce.js";
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, spacingVars, radiusVars, durationVars, easeVars, typographyVars, fontWeightVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { isOptionData, isDivider, isSection, normalizeOption, getSelectableOptions } from "./utils.js";
import { useCombobox, useSelectedItemOffset } from "./hooks.js";
import { SelectorOption } from "./SelectorOption.js";
import { getInputARIA, mergeProps } from "../utils/index.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { themeProps } from "../utils/themeProps.js";
import { groupStyles } from "../InputGroup/groupStyles.js";
import { useInputGroup } from "../InputGroup/InputGroupContext.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { useTranslator } from "../i18n/index.js";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const styles = {
  triggerContainer: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x1txdalj",
    kzqmXN: "xh8yej3",
    k8WAf4: "xce4md1",
    kg3NbH: "xrrkdod",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib xdk3u5w",
    kLWn49: "x1kq96og",
    kMwMTN: "x1tgivj0",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  triggerPlaceholder: {
    kMwMTN: "xv1l7n4",
    $$css: true
  },
  triggerGhost: {
    kzqmXN: "x14atkfc",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kKwaWg: "x1ilzqfv xq8i9tn",
    kGVxlE: "x1gnnqk1 x1irk71m x15ysqaf",
    k63SB2: "x1e4wzip",
    kI3sdo: "x1a2a7pz x1irc7jg",
    kInvED: "x1wfwxd8 xscfbxq",
    k1ekBW: "xrafxwg",
    k3aq6I: "x3oybdh xk4oym4",
    $$css: true
  },
  triggerGhostDisabled: {
    kKwaWg: "x18o3ruo",
    k3aq6I: "x1c071of x1pdlv7q",
    $$css: true
  },
  popover: {
    k7Eaqz: "xrzjruh",
    $$css: true
  },
  sectionDivider: {
    kqGvvJ: "xsq74q5",
    $$css: true
  },
  divider: {
    kqGvvJ: "xsq74q5",
    $$css: true
  },
  item: {
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x1txdalj",
    kzqmXN: "xh8yej3",
    kmVPX3: "xlsj2fj",
    kaIpWk: "xh6dtrn",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib",
    kMwMTN: "x1tgivj0",
    kWkggS: "xjbqb8w",
    kkrTdU: "x1ypdohk",
    k9WMMc: "x1yc453h",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  itemHighlighted: {
    kWkggS: "x1lmrjuc",
    $$css: true
  },
  itemSelected: {
    k63SB2: "x1e4wzip",
    $$css: true
  },
  itemDisabled: {
    kSiTet: "xbyyjgo",
    kkrTdU: "x1h6gzvc",
    $$css: true
  }
};
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};

/**
 * Size-specific overrides for dropdown list items.
 * Matches the pattern used by DropdownMenuItem so that
 * an `sm` selector renders compact list items, `md`/`lg` use
 * the base padding defined in `styles.item`.
 */
const itemSizeStyles = {
  sm: {
    k8WAf4: "xu0wf1k",
    kg3NbH: "xf314gf",
    $$css: true
  },
  md: {
    k8WAf4: "x1vofgu7",
    $$css: true
  },
  lg: {
    $$css: true
  }
};
const STATUS_ICON_MAP = {
  warning: 'warning',
  error: 'error',
  success: 'success'
};
const STATUS_ICON_COLOR_MAP = {
  warning: 'warning',
  error: 'error',
  success: 'success'
};
const STATUS_BUTTON_LABEL_KEY = {
  warning: '@astryx.input.statusButton.warning',
  error: '@astryx.input.statusButton.error',
  success: '@astryx.input.statusButton.success'
};

/**
 * Without `hasClear`, the selector always has a string value (or undefined for placeholder).
 * With `hasClear`, the value can be `null` and onChange receives `null` on clear.
 */

/**
 * Default option renderer
 */
function DefaultOption({
  option
}) {
  return /*#__PURE__*/_jsx(SelectorOption, {
    icon: option.icon,
    label: option.label ?? option.value
  });
}

// Case-insensitive substring match for a single option. The one predicate used
// by both the flat filter (count + keyboard nav) and the grouped renderer, so
// what is shown while searching stays in lockstep with the announced count.
function optionMatchesQuery(option, query) {
  if (!query) {
    return true;
  }
  return (option.label ?? option.value).toLowerCase().includes(query.toLowerCase());
}

// Case-insensitive substring filter over the selectable options. Shared by the
// `filteredItems` memo (rendering) and the search-change handler, which needs
// the count for the *next* query synchronously to announce it exactly once per
// keystroke rather than reacting to state in an effect.
function filterOptionsByQuery(items, query) {
  if (!query) {
    return items;
  }
  return items.filter(item => optionMatchesQuery(item, query));
}

/**
 * A selector/dropdown component for choosing from a list of options.
 *
 * @example
 * ```
 * <Selector
 *   label="Fruit"
 *   options={['Apple', 'Banana', 'Orange']}
 *   value={fruit}
 *   onChange={setFruit}
 *   placeholder="Select a fruit..."
 * />
 * ```
 */
export function Selector(props) {
  const t = useTranslator();
  const {
    label,
    isLabelHidden = false,
    description,
    isOptional = false,
    isRequired = false,
    isDisabled = false,
    disabledMessage,
    options,
    value,
    onChange,
    changeAction,
    isLoading = false,
    placeholder: placeholderFromProps,
    size: sizeProp,
    variant = 'input',
    status,
    statusVariant = 'attached',
    labelTooltip,
    startIcon,
    htmlName,
    renderOption,
    hasSearch = false,
    searchPlaceholder: searchPlaceholderFromProps,
    placement,
    isDefaultOpen = false,
    'data-testid': testId,
    width,
    xstyle,
    className,
    style,
    hasClear: hasClearProp,
    ...rest
  } = props;
  const placeholder = placeholderFromProps ?? t('@astryx.selector.placeholder');
  const searchPlaceholder = searchPlaceholderFromProps ?? t('@astryx.selector.searchPlaceholder');
  const hasClear = hasClearProp === true;
  const size = useSize(sizeProp, 'md');
  const effectiveStatusVariant = variant === 'ghost' && statusVariant === 'attached' ? 'detached' : statusVariant;

  // Normalize null to undefined for internal use (null is the clear sentinel)
  const normalizedValue = value === null ? undefined : value;
  const triggerId = useId();
  const listboxId = useId();
  const descriptionId = useId();
  const statusMessageId = useId();
  const inputLabelId = useId();
  const searchId = useId();
  // Measure from the same outer control that usePopover anchors to; using the
  // shorter inner button makes every size's selected row land too low.
  const anchorRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const inputGroup = useInputGroup();
  const [searchQuery, setSearchQuery] = useState('');
  // A typed query shows TextInput's built-in clear (✕) button, which becomes
  // the next tab stop after the search input.
  const hasQuery = searchQuery.length > 0;
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(normalizedValue);
  const isBusy = isLoading || optimisticValue !== normalizedValue;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the trigger container (which already exists)
  // and the trigger button stays perceivable via aria-disabled instead of the
  // disabled attribute. Activation is blocked by the isDisabled guards in
  // useCombobox (onTriggerClick / onKeyDown).
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container div is not naturally focusable; focusin bubbles up from
    // the trigger button, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const statusTooltip = useTooltip({
    placement: 'above',
    isEnabled: effectiveStatusVariant === 'tooltip' && !!status?.message
  });
  const {
    ariaLabelledBy,
    ariaDescribedBy
  } = getInputARIA(inputLabelId, [description ? descriptionId : null, !inputGroup && effectiveStatusVariant !== 'tooltip' && status?.message ? statusMessageId : null, effectiveStatusVariant === 'tooltip' && status?.message ? statusTooltip.describedBy : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null], inputGroup);

  // Flatten options for keyboard navigation
  const selectableItems = useMemo(() => getSelectableOptions(options), [options]);

  // Filter items by search query
  const filteredItems = useMemo(() => filterOptionsByQuery(selectableItems, searchQuery), [selectableItems, searchQuery]);

  // Find selected item and its index for positioning
  const selectedItemIndex = useMemo(() => {
    return selectableItems.findIndex(item => item.value === optimisticValue);
  }, [selectableItems, optimisticValue]);
  const selectedItem = useMemo(() => {
    return selectedItemIndex >= 0 ? selectableItems[selectedItemIndex] : undefined;
  }, [selectableItems, selectedItemIndex]);

  // Ref for listbox to measure selected item position
  const listboxRef = useRef(null);

  // Announce match counts / "No results found" politely as the user types, so
  // screen-reader users hear how many options remain. Filtering was previously
  // silent (comboboxes-7). Mirrors BaseTypeahead, which announces from its
  // query-change callback (not a reactive effect) via the same useAnnounce hook.
  const announce = useAnnounce();

  // Layer for dropdown positioning
  const handleLayerHide = useCallback(() => {
    setSearchQuery('');
    // Clear any lingering result count when the popover closes so stale status
    // text does not linger in the a11y tree.
    announce('');
    triggerRef.current?.focus();
  }, [announce]);
  const popover = usePopover({
    onHide: handleLayerHide,
    hasLightDismiss: true,
    hasCloseButton: false,
    hasAutoFocus: false,
    // The popup's own role="listbox" is the exposed semantics; the trigger
    // keeps DOM focus, so wrapping it in a modal dialog would misrepresent it.
    role: 'none'
  });

  // Open dropdown on mount when isDefaultOpen is true
  useEffect(() => {
    if (isDefaultOpen) {
      popover.show();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mount-only: isDefaultOpen is not reactive
  }, []);

  // Announce the filtered result count from the query-change handler (matching
  // BaseTypeahead) rather than a reactive effect: computing the count for the
  // next query here fires the announcement exactly once per keystroke and does
  // not re-speak on unrelated re-renders.
  const handleSearchChange = useCallback(nextQuery => {
    setSearchQuery(nextQuery);
    if (nextQuery.length === 0) {
      // Emptying the query clears the region rather than announcing a count.
      announce('');
      return;
    }
    const count = filterOptionsByQuery(selectableItems, nextQuery).length;
    announce(count === 0 ? 'No results found' : `${count} result${count === 1 ? '' : 's'}`);
  }, [announce, selectableItems]);

  // Calculate offset to position selected item over trigger. Explicit
  // placement opts out of the selector-specific overlay behavior and uses the
  // standard layer positioning API instead.
  const shouldOverlaySelectedItem = placement == null && !hasSearch;
  const {
    offset: rawOffset,
    isPositioned: rawIsPositioned
  } = useSelectedItemOffset({
    isOpen: popover.isOpen && shouldOverlaySelectedItem,
    selectedItemIndex,
    listboxId,
    listboxRef,
    anchorRef
  });
  const selectedItemOffset = shouldOverlaySelectedItem ? rawOffset : 0;
  const isPositioned = shouldOverlaySelectedItem ? rawIsPositioned : true;
  const popoverPlacement = placement ?? 'below';
  const popoverOffsetStyle = selectedItemOffset > 0 ? {
    marginBlockStart: `-${selectedItemOffset}px`
  } : undefined;

  // Clear the current value. Shared by the clear button and the keyboard
  // Delete/Backspace path so clearing is reachable without a mouse.
  const clearValue = useCallback(() => {
    onChange?.(null);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(undefined);
        await changeAction(null);
      });
    }
  }, [onChange, changeAction, startTransition, setOptimisticValue]);

  // Selector behavior (keyboard nav, typeahead, selection)
  const {
    highlightedIndex,
    setHighlightedIndex: _setHighlightedIndex,
    getItemId,
    onTriggerClick,
    onKeyDown,
    onItemSelect,
    onItemMouseEnter
  } = useCombobox({
    selectableItems: filteredItems,
    value: normalizedValue,
    isDisabled,
    isOpen: popover.isOpen,
    hasSearch,
    onOpen: useCallback(() => {
      popover.show();
      if (hasSearch) {
        requestAnimationFrame(() => {
          searchRef.current?.focus();
        });
      }
    }, [popover, hasSearch]),
    onClose: popover.hide,
    onSelect: useCallback(newValue => {
      onChange?.(newValue);
      if (changeAction) {
        startTransition(async () => {
          setOptimisticValue(newValue);
          await changeAction(newValue);
        });
      }
    }, [onChange, changeAction, startTransition, setOptimisticValue]),
    onClear: hasClear ? clearValue : undefined,
    listboxId
  });

  // Keep the highlighted option visible during keyboard navigation. The
  // listbox is a fixed-height scroll container, so without this the virtual
  // cursor walks off-screen once navigation passes the visible window. Mirrors
  // CommandPaletteItem's scrollIntoView({block: 'nearest'}) behavior.
  useEffect(() => {
    if (!popover.isOpen || highlightedIndex < 0) {
      return;
    }
    document.getElementById(getItemId(highlightedIndex))?.scrollIntoView?.({
      block: 'nearest'
    });
  }, [popover.isOpen, highlightedIndex, getItemId]);

  // Handle clear button click
  const handleClear = useCallback(e => {
    e.stopPropagation(); // Don't open dropdown
    clearValue();
  }, [clearValue]);

  // Render search input
  const renderSearch = useCallback(() => {
    if (!hasSearch) {
      return null;
    }
    return /*#__PURE__*/_jsx("div", {
      ...{
        className: "x78zum5 x6s0dn4 xf314gf xu0wf1k"
      },
      onKeyDown: e => {
        // The clear (✕) button lives inside the TextInput, after the input in
        // DOM order. When it is focused and the user tabs forward there is
        // nothing else in the popup, so dismiss it (Shift+Tab returns to the
        // input natively). Key events originating on the input are handled on
        // the input below; ignore them here so we don't double-dismiss.
        if (e.target === searchRef.current) {
          return;
        }
        if (e.key === 'Tab' && !e.shiftKey) {
          onKeyDown(e);
        }
      },
      children: /*#__PURE__*/_jsx(TextInput, {
        ref: searchRef,
        id: searchId
        // The search field IS a TextInput: the leading magnifier is its
        // `startIcon` and the trailing clear (✕) is its built-in `hasClear`
        // (which resets the value and refocuses the input). We add no bespoke
        // affordance chrome — the field just looks and behaves like every
        // other Astryx input.
        ,
        label: t('@astryx.selector.searchOptions'),
        isLabelHidden: true,
        startIcon: "search",
        hasClear: true,
        size: "sm"
        // Fill the dropdown's width (minus the wrapper's inline padding) so
        // the field is flush end-to-end rather than sized to its content.
        ,
        width: "100%"
        // When hasSearch is set, focus moves into this input on open, so it —
        // not the trigger — must be the combobox that reports the highlighted
        // option via aria-activedescendant (comboboxes-4). A bare searchbox
        // left the highlight silent to screen readers. role + aria-* pass
        // through to the underlying <input> via BaseProps.
        ,
        role: "combobox",
        "aria-expanded": popover.isOpen,
        "aria-controls": listboxId,
        "aria-autocomplete": "list",
        "aria-activedescendant": popover.isOpen && highlightedIndex >= 0 ? getItemId(highlightedIndex) : undefined,
        value: searchQuery,
        onChange: handleSearchChange,
        onKeyDown: e => {
          // Arrow keys navigate options; Enter selects; Escape closes.
          // Home/End are left to the input for caret movement (APG editable
          // combobox); PageUp/PageDown are the sanctioned substitute for
          // jumping to the first/last option.
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'PageDown' || e.key === 'Enter' || e.key === 'Escape') {
            onKeyDown(e);
            return;
          }
          // Tab: when a query is showing the clear (✕) button, forward-tab
          // moves focus to it (keeping the popup open) so the affordance is
          // keyboard-reachable. Every other Tab dismisses the popup as usual.
          if (e.key === 'Tab' && (e.shiftKey || !hasQuery)) {
            onKeyDown(e);
          }
        },
        placeholder: searchPlaceholder
      })
    });
  }, [hasSearch, searchId, listboxId, searchQuery, hasQuery, searchPlaceholder, handleSearchChange, onKeyDown, popover.isOpen, highlightedIndex, getItemId, t]);

  // Render an individual item
  const renderItem = useCallback((item, flatIndex) => {
    const isHighlighted = flatIndex === highlightedIndex;
    const isSelected = item.value === normalizedValue;
    return /*#__PURE__*/_jsxs("div", {
      id: getItemId(flatIndex),
      role: "option",
      "aria-selected": isSelected,
      "aria-disabled": item.disabled,
      onClick: () => onItemSelect(item),
      onMouseEnter: () => onItemMouseEnter(item, flatIndex),
      ...stylex.props(styles.item, itemSizeStyles[size], isHighlighted && styles.itemHighlighted, isSelected && styles.itemSelected, item.disabled && styles.itemDisabled),
      children: [/*#__PURE__*/_jsx("span", {
        ...{
          className: "x78zum5 x6s0dn4 x1txdalj x98rzlu xeuugli"
        },
        children: renderOption ? renderOption(item) : /*#__PURE__*/_jsx(DefaultOption, {
          option: item
        })
      }), isSelected && /*#__PURE__*/_jsx(Icon, {
        icon: "check",
        size: "sm",
        color: "accent"
        // Stable theme target on the selected-row marker, so a theme can
        // restyle or hide it (e.g. to compose its own selected indicator
        // via renderOption) without a structural sibling selector.
        ,
        ...themeProps('selector-check')
      })]
    }, item.value);
  }, [renderOption, highlightedIndex, size, normalizedValue, getItemId, onItemSelect, onItemMouseEnter]);

  // Render all options (handling sections/dividers)
  const renderOptions = useCallback(() => {
    const isSearching = hasSearch && Boolean(searchQuery);

    // Nothing matched across every group/option: show the empty state.
    if (isSearching && filteredItems.length === 0) {
      // role="presentation" keeps the message out of the listbox's
      // accessibility tree (role="listbox" only permits option/group
      // children); the no-results outcome is announced via the
      // result-count live region instead.
      return [/*#__PURE__*/_jsx("div", {
        role: "presentation",
        ...mergeProps(themeProps('selector-empty-state'), {
          className: "x1b2ylru x2b8uid xv1l7n4 x9ynric xcr08ib"
        }),
        children: "No results found"
      }, "empty")];
    }
    let flatIndex = 0;
    const elements = [];
    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      if (isDivider(option)) {
        // While searching, a standalone divider between groups would orphan
        // itself once its neighbors are filtered out, so skip it.
        if (isSearching) {
          continue;
        }
        elements.push(/*#__PURE__*/_jsx(Divider, {
          xstyle: styles.divider
        }, `divider-${i}`));
      } else if (isSection(option)) {
        const sectionItems = [];
        for (const opt of option.options) {
          const normalized = normalizeOption(opt);
          if (isSearching && !optionMatchesQuery(normalized, searchQuery)) {
            continue;
          }
          sectionItems.push(renderItem(normalized, flatIndex));
          flatIndex++;
        }
        // Hide a group entirely (header + wrapper) when none of its items
        // match the query, so no header is left standing over nothing.
        if (sectionItems.length === 0) {
          continue;
        }
        if (option.title) {
          elements.push(/*#__PURE__*/_jsx(Divider, {
            label: option.title,
            xstyle: styles.sectionDivider
          }, `section-divider-${i}`));
        }
        elements.push(/*#__PURE__*/_jsx("div", {
          role: "group",
          "aria-label": option.title,
          children: sectionItems
        }, `section-${i}`));
      } else if (isOptionData(option)) {
        const normalized = normalizeOption(option);
        if (isSearching && !optionMatchesQuery(normalized, searchQuery)) {
          continue;
        }
        elements.push(renderItem(normalized, flatIndex));
        flatIndex++;
      }
    }
    return elements;
  }, [options, renderItem, hasSearch, searchQuery, filteredItems]);

  // The detached message box renders its own leading status icon, so the
  // on-field icon would duplicate it — keep the chevron indicator instead.
  const showStatusIcon = status != null && effectiveStatusVariant !== 'detached';
  const showStatusTooltip = status != null && effectiveStatusVariant === 'tooltip' && !!status.message;
  const selectorContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        anchorRef.current = el;
        popover.triggerRef(el);
        // Anchor + hover/focus listeners for the disabled-message tooltip.
        // Handlers are gated internally by isEnabled, and anchor names
        // compose, so attaching unconditionally is safe.
        disabledMessageTooltip.ref(el);
      },
      onClick: onTriggerClick,
      "data-testid": testId,
      ...mergeProps(themeProps('selector', {
        variant,
        size,
        status: status?.type ?? null
      }), stylex.props(inputWrapperStyles.base, styles.triggerContainer, sizeStyles[size], variant === 'ghost' && styles.triggerGhost, isDisabled && inputWrapperStyles.disabled, variant === 'ghost' && isDisabled && styles.triggerGhostDisabled, !selectedItem && styles.triggerPlaceholder, variant !== 'ghost' && status && inputStatusBorderStyles[status.type], variant !== 'ghost' && status && !isDisabled && inputStatusHoverShadowStyles[status.type], variant !== 'ghost' && inputGroup && groupStyles.inGroup, xstyle), className, style),
      children: [startIcon && renderIconSlot(startIcon, {
        size: 'sm',
        color: 'secondary'
      }), inputGroup && /*#__PURE__*/_jsx(VisuallyHidden, {
        id: inputLabelId,
        children: label
      }), /*#__PURE__*/_jsx("button", {
        ref: triggerRef,
        id: triggerId,
        type: "button"
        // In hasSearch mode the popup's search input is the combobox (it owns
        // focus + aria-activedescendant, comboboxes-4), so the trigger is a
        // plain button that opens the listbox — not a second combobox.
        ,
        role: hasSearch ? undefined : 'combobox',
        ...rest,
        "aria-haspopup": "listbox",
        "aria-expanded": popover.isOpen,
        "aria-controls": listboxId,
        "aria-activedescendant": !hasSearch && popover.isOpen && highlightedIndex >= 0 ? getItemId(highlightedIndex) : undefined,
        "aria-describedby": ariaDescribedBy,
        "aria-labelledby": ariaLabelledBy,
        "aria-required": isRequired ? 'true' : undefined,
        "aria-invalid": status?.type === 'error' ? 'true' : undefined,
        "aria-busy": isBusy || undefined
        // With a disabledMessage the trigger keeps focusability via
        // aria-disabled so the reason is focus-discoverable; activation is
        // still blocked by the isDisabled guards in useCombobox.
        ,
        disabled: isDisabled && !showsDisabledMessage,
        "aria-disabled": showsDisabledMessage ? 'true' : undefined,
        onKeyDown: onKeyDown,
        tabIndex: isDisabled && !showsDisabledMessage ? -1 : 0,
        ...{
          className: "x78zum5 x6s0dn4 x1qughib x1txdalj x1iyjqo2 xs83m0k x1r8uery xeuugli x1717udv x1ghz6dp xc342km xng3xce xjbqb8w xjb2p0i x1qlqyl8 x15bjb6t x1heor9g x1ypdohk x1a2a7pz"
        },
        children: /*#__PURE__*/_jsx("span", {
          ...{
            className: "x1iyjqo2 xeuugli xb3r6kr xlyipyv xuxw1ft x1yc453h"
          },
          children: selectedItem?.label ?? placeholder
        })
      }), htmlName != null && /*#__PURE__*/_jsx("input", {
        type: "hidden",
        name: htmlName,
        value: value ?? ''
        // Disabled native controls are excluded from form submission;
        // mirror that for the hidden carrier.
        ,
        disabled: isDisabled
      }), isBusy && /*#__PURE__*/_jsx(Spinner, {
        size: "sm"
      }), hasClear && value != null && !isDisabled && /*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: handleClear,
        "aria-label": t('@astryx.selector.clearLabel', {
          label
        }),
        ...{
          className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
        },
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "close",
          size: "sm",
          color: "secondary"
          // Stable theme target on the clear glyph itself, so a theme can
          // restyle just this icon (color, size, hover) via `defineTheme`.
          // Same-element rules in @layer astryx-theme win over the icon's
          // own base color/size, which a button-level target could not
          // reach.
          ,
          ...themeProps('selector-clear-icon')
        })
      }), /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x1kky2od xlup9mm x11xpdln xuedmi6 xlr8y92 x1g0ag68 xv9yike"
          },
          2: {
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x1kky2od xlup9mm x11xpdln xuedmi6 xlr8y92 x1g0ag68 xv9yike x19jd1h0"
          },
          1: {
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x1kky2od xlup9mm x11xpdln xuedmi6 xlr8y92 x1g0ag68 xv9yike xq2gx43"
          },
          3: {
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x1kky2od xlup9mm x11xpdln xuedmi6 xlr8y92 x1g0ag68 xv9yike x19jd1h0 xq2gx43"
          }
        }[!!(!showStatusIcon && popover.isOpen) << 1 | !!showStatusIcon << 0],
        children: showStatusIcon ? showStatusTooltip ? /*#__PURE__*/_jsx("button", {
          ref: statusTooltip.ref,
          type: "button",
          "aria-label": t(STATUS_BUTTON_LABEL_KEY[status.type]),
          "aria-describedby": statusTooltip.describedBy,
          onClick: e => e.stopPropagation(),
          ...{
            className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1heor9g x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
          },
          children: /*#__PURE__*/_jsx(Icon, {
            icon: STATUS_ICON_MAP[status.type],
            size: "sm",
            color: STATUS_ICON_COLOR_MAP[status.type]
          })
        }) : /*#__PURE__*/_jsx(Icon, {
          icon: STATUS_ICON_MAP[status.type],
          size: "sm",
          color: STATUS_ICON_COLOR_MAP[status.type]
        }) : /*#__PURE__*/_jsx(Icon, {
          icon: "chevronDown",
          size: "sm",
          color: "inherit"
          // Stable theme target on the chevron glyph itself, so a theme can
          // restyle just this icon (color, size, hover) — and its
          // open/closed state — via `defineTheme`. Same-element rules in
          // @layer astryx-theme win over the icon's own base color/size,
          // which a button-level target could not reach.
          ,
          ...themeProps('selector-indicator-icon', {
            state: popover.isOpen ? 'expanded' : 'collapsed'
          })
        })
      })]
    }), popover.render(hasSearch ? /*#__PURE__*/_jsxs("div", {
      children: [renderSearch(), /*#__PURE__*/_jsx("div", {
        ref: listboxRef,
        id: listboxId,
        role: "listbox",
        "aria-labelledby": triggerId,
        ...{
          0: {
            className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x7a5moj x1hc1fzr x1ey1afo"
          },
          1: {
            className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x1hc1fzr x1ey1afo x2hg6jq"
          }
        }[!!(variant !== 'ghost') << 0],
        children: renderOptions()
      })]
    }) : /*#__PURE__*/_jsx("div", {
      ref: listboxRef,
      id: listboxId,
      role: "listbox",
      "aria-labelledby": triggerId,
      ...{
        0: {
          className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x7a5moj x1hc1fzr x1ey1afo"
        },
        2: {
          className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x1hc1fzr x1ey1afo x2hg6jq"
        },
        1: {
          className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x7a5moj xg01cxk xq2gx43"
        },
        3: {
          className: "x9f619 xuyqlj2 x1odjw0f xu0wf1k x2hg6jq xg01cxk xq2gx43"
        }
      }[!!(variant !== 'ghost') << 1 | !!!isPositioned << 0],
      children: renderOptions()
    }), {
      placement: popoverPlacement,
      alignment: 'start',
      xstyle: [styles.popover, layerAnimations[popoverPlacement]],
      style: popoverOffsetStyle
    }), showStatusTooltip && statusTooltip.renderTooltip(status?.message ?? ''), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
  if (inputGroup) {
    return selectorContent;
  }
  return /*#__PURE__*/_jsx(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: triggerId,
    descriptionID: description ? descriptionId : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageId : undefined
    } : undefined,
    statusVariant: effectiveStatusVariant,
    labelTooltip: labelTooltip,
    width: width,
    children: selectorContent
  });
}
Selector.displayName = 'Selector';